import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = 'https://aljodia.com/api';
const isRtl = localStorage.getItem('language') === 'ar';

export enum ReturnStatus {
  PENDING = 'pending_approval',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export interface ApprovedItem {
  itemId: string;
  approvedQuantity: number;
  status: 'approved' | 'rejected' | 'partial';
}

interface ReturnsApiError {
  message: string;
  status?: number;
}

const returnsAxios = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

const handleError = async (error: AxiosError, originalRequest: AxiosRequestConfig): Promise<never> => {
  const errorDetails = {
    url: error.config?.url,
    method: error.config?.method,
    status: error.response?.status,
    data: error.response?.data,
    message: error.message,
  };
  
  console.error(`[${new Date().toISOString()}] Returns API response error:`, errorDetails);
  
  let message = (error.response?.data as any)?.message || (isRtl ? 'خطأ غير متوقع' : 'Unexpected error');
  
  switch (error.response?.status) {
    case 400:
      message = (error.response?.data as any)?.message || (isRtl ? 'بيانات غير صالحة' : 'Invalid data');
      break;
    case 403:
      message = (error.response?.data as any)?.message || (isRtl ? 'عملية غير مصرح بها' : 'Unauthorized operation');
      break;
    case 404:
      message = (error.response?.data as any)?.message || (isRtl ? 'الإرجاع غير موجود' : 'Return not found');
      break;
    case 409:
      message = (error.response?.data as any)?.message || (isRtl ? 'تعارض في الكتابة، حاول مرة أخرى' : 'Write conflict, try again');
      break;
    case 422:
      message = (error.response?.data as any)?.message || (isRtl ? 'الكمية غير كافية' : 'Insufficient quantity');
      break;
    case 429:
      message = isRtl ? 'طلبات كثيرة جدًا، حاول مرة أخرى لاحقًا' : 'Too many requests, try again later';
      break;
    default:
      if (!navigator.onLine) {
        message = isRtl ? 'لا يوجد اتصال بالإنترنت' : 'No internet connection';
      }
  }
  
  if (error.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.error(`[${new Date().toISOString()}] No refresh token available`);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        toast.error(isRtl ? 'التوكن منتهي الصلاحية، يرجى تسجيل الدخول مجددًا' : 'Token expired, please log in again', {
          position: isRtl ? 'top-right' : 'top-left',
          autoClose: 3000,
        });
        throw new Error(isRtl ? 'التوكن منتهي الصلاحية ولا يوجد توكن منعش' : 'Token expired and no refresh token available');
      }
      
      const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, { refreshToken });
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      localStorage.setItem('token', accessToken);
      if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
      
      console.log(`[${new Date().toISOString()}] Token refreshed successfully`);
      originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${accessToken}` };
      return returnsAxios(originalRequest);
    } catch (refreshError) {
      console.error(`[${new Date().toISOString()}] Refresh token failed:`, refreshError);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
      toast.error(isRtl ? 'فشل تجديد التوكن، يرجى تسجيل الدخول مجددًا' : 'Failed to refresh token, please log in again', {
        position: isRtl ? 'top-right' : 'top-left',
        autoClose: 3000,
      });
      throw new Error(isRtl ? 'فشل تجديد التوكن' : 'Failed to refresh token');
    }
  }
  
  toast.error(message, { position: isRtl ? 'top-right' : 'top-left', autoClose: 3000 });
  throw { message, status: error.response?.status } as ReturnsApiError;
};

returnsAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    
    const language = localStorage.getItem('language') || 'en';
    config.params = { ...config.params, lang: language };
    
    console.log(`[${new Date().toISOString()}] Returns API request:`, {
      url: config.url,
      method: config.method,
      params: config.params,
    });
    
    return config;
  },
  (error) => {
    console.error(`[${new Date().toISOString()}] Returns API request error:`, error);
    return Promise.reject(error);
  }
);

returnsAxios.interceptors.response.use(
  (response) => {
    if (!response.data) {
      console.error(`[${new Date().toISOString()}] Empty response data:`, response);
      throw new Error(isRtl ? 'استجابة فارغة من الخادم' : 'Empty response from server');
    }
    return response.data;
  },
  (error) => handleError(error, error.config)
);

const isValidObjectId = (id: string): boolean => /^[0-9a-fA-F]{24}$/.test(id);

export const returnsAPI = {
  createReturn: async (returnData: {
    branchId: string;
    items: Array<{ product: string; quantity: number; reason: string; reasonEn?: string }>;
    notes?: string;
  }) => {
    console.log('[returnsAPI.createReturn] Sending:', returnData);
    
    const payload = {
      branchId: returnData.branchId,
      items: returnData.items.map(i => ({
        product: i.product,
        quantity: i.quantity,
        reason: i.reason,
        reasonEn: i.reasonEn,
      })),
      notes: returnData.notes,
    };
    
    const response = await returnsAxios.post('/returns', payload);
    
    if (response && response.status !== ReturnStatus.PENDING) {
      console.warn('[returnsAPI.createReturn] Warning: Return status is not pending_approval:', response.status);
      if (response.statusHistory && response.statusHistory[0]?.status === ReturnStatus.PENDING) {
        console.log('[returnsAPI.createReturn] Correcting status to pending_approval');
        response.status = ReturnStatus.PENDING;
      }
    }
    
    return response;
  },
  
  getAll: async (params: { 
    page?: number; 
    limit?: number; 
    branch?: string; 
    status?: string; 
    reason?: string; 
    search?: string;
  }) => {
    console.log(`[${new Date().toISOString()}] returnsAPI.getAll - Sending:`, params);
    
    if (params.branch && !isValidObjectId(params.branch)) {
      console.error(`[${new Date().toISOString()}] returnsAPI.getAll - Invalid branch ID:`, params.branch);
      throw new Error(isRtl ? 'معرف الفرع غير صالح' : 'Invalid branch ID');
    }
    
    const response = await returnsAxios.get('/returns', { params });
    
    console.log(`[${new Date().toISOString()}] returnsAPI.getAll - Success:`, {
      total: response.total,
      returnsCount: response.returns?.length,
    });
    
    return response;
  },
  
  getById: async (id: string) => {
    console.log(`[${new Date().toISOString()}] returnsAPI.getById - Sending:`, { id });
    
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] returnsAPI.getById - Invalid return ID:`, id);
      throw new Error(isRtl ? 'معرف الإرجاع غير صالح' : 'Invalid return ID');
    }
    
    const response = await returnsAxios.get(`/returns/${id}`);
    
    console.log(`[${new Date().toISOString()}] returnsAPI.getById - Success:`, response);
    return response;
  },
  
  getBranches: async () => {
    console.log(`[${new Date().toISOString()}] returnsAPI.getBranches - Sending request`);
    
    const response = await returnsAxios.get('/returns/branches');
    
    console.log(`[${new Date().toISOString()}] returnsAPI.getBranches - Success:`, { 
      branchesCount: response.branches?.length 
    });
    
    return response;
  },
  
  updateReturnStatus: async (
    id: string, 
    status: ReturnStatus, 
    reviewNotes?: string,
    approvedItems?: ApprovedItem[]
  ) => {
    console.log(`[${new Date().toISOString()}] returnsAPI.updateReturnStatus - Sending:`, { 
      id, 
      status,
      reviewNotes,
      approvedItems 
    });
    
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] returnsAPI.updateReturnStatus - Invalid return ID:`, id);
      throw new Error(isRtl ? 'معرف الإرجاع غير صالح' : 'Invalid return ID');
    }
    
    const payload: any = {
      reviewNotes: reviewNotes || ''
    };
    
    if (approvedItems && Array.isArray(approvedItems) && approvedItems.length > 0) {
      payload.approvedItems = approvedItems.map(item => ({
        itemId: item.itemId,
        approvedQuantity: item.approvedQuantity
      }));
      
      payload.status = 'approved';
    } else {
      payload.status = status;
    }
    
    console.log(`[${new Date().toISOString()}] returnsAPI.updateReturnStatus - Final Payload:`, payload);
    
    try {
      const response = await returnsAxios.put(`/returns/${id}`, payload);
      console.log(`[${new Date().toISOString()}] returnsAPI.updateReturnStatus - Success:`, response);
      return response;
    } catch (error: any) {
      console.error(`[${new Date().toISOString()}] returnsAPI.updateReturnStatus - Error:`, error);
      throw error;
    }
  },
  
  approveReturn: async (id: string, reviewNotes?: string) => {
    return returnsAPI.updateReturnStatus(id, ReturnStatus.APPROVED, reviewNotes);
  },
  
  rejectReturn: async (id: string, reviewNotes?: string) => {
    return returnsAPI.updateReturnStatus(id, ReturnStatus.REJECTED, reviewNotes);
  },
  
  deleteReturn: async (id: string) => {
    console.log(`[${new Date().toISOString()}] returnsAPI.deleteReturn - Sending:`, { id });
    
    if (!isValidObjectId(id)) {
      console.error(`[${new Date().toISOString()}] returnsAPI.deleteReturn - Invalid return ID:`, id);
      throw new Error(isRtl ? 'معرف الإرجاع غير صالح' : 'Invalid return ID');
    }
    
    const response = await returnsAxios.delete(`/returns/${id}`);
    
    console.log(`[${new Date().toISOString()}] returnsAPI.deleteReturn - Success:`, response);
    return response;
  },
};

export default returnsAPI;